/**
 * State for the self-funding checkout step.
 *
 * Paying: mounts the Checkout Session the paywall created (or creates it) for a consumer who can buy.
 * Returning from Stripe (`checkout=complete`): confirms the purchase and, once access is active, goes
 * on with `replace` (so Back does not land on a finished checkout) into the self-funding sequence at
 * the step last reached, so renewing after expiry returns the consumer to where they were; a renewal
 * started from Your Plan (`then=your-plan`) goes back to Your Plan instead, and choosing to self-fund
 * from the options (`then=self-fund-choice`) goes on to that choice (`useSelfFundChoicePath`). If
 * confirmation times out, Back leaves for Your Plan after a renewal started there, or else the options,
 * rather than the paywall, which would offer to buy again before the payment is recorded. Already active (a typed
 * URL, a second tab, or a 409 from Stripe's session): no checkout, a message and a way on.
 */
import { useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { formatCurrency } from '@shared';
import { resolveSelfFundResumeStep } from '../lib/self-fund-access';
import { useConfirmSelfFundAccess } from './use-confirm-self-fund-access';
import { useConsumerFlow } from './use-consumer-flow';
import { useConsumerPlan } from './use-consumer-plan';
import { useSelfFundAccess } from './use-self-fund-access';
import { useSelfFundChoicePath } from './use-self-fund-choice-path';
import {
  AFTER_PURCHASE_PARAM,
  CHECKOUT_COMPLETE_PARAM,
  afterPurchaseFor,
  parseAfterPurchase,
  useSelfFundCheckoutSession,
} from './use-self-fund-checkout-session';

export function useSelfFundCheckoutStep(clientId: string) {
  const [searchParams] = useSearchParams();
  const { back, finish, goTo, returnTo } = useConsumerFlow(clientId);
  const { access, isConsumerSession } = useSelfFundAccess();
  const { plan } = useConsumerPlan(clientId);
  const isReturning = searchParams.get(CHECKOUT_COMPLETE_PARAM) === 'complete';
  const afterPurchase = isReturning
    ? parseAfterPurchase(searchParams.get(AFTER_PURCHASE_PARAM))
    : afterPurchaseFor(returnTo);
  const navigate = useNavigate();
  const selfFundChoicePath = useSelfFundChoicePath(clientId);
  const resumeStep =
    afterPurchase === 'your-plan' ? afterPurchase : resolveSelfFundResumeStep(plan?.lastStep);
  const canBuy = isConsumerSession && access !== 'active' && !isReturning;

  const checkout = useSelfFundCheckoutSession(clientId, { enabled: canBuy, afterPurchase });
  const confirmation = useConfirmSelfFundAccess(isReturning);

  const goOn = useCallback(
    (options: { replace?: boolean; state?: Record<string, unknown> } = {}) =>
      afterPurchase === 'self-fund-choice'
        ? navigate(selfFundChoicePath, options)
        : goTo(resumeStep, options),
    [afterPurchase, navigate, selfFundChoicePath, goTo, resumeStep],
  );

  useEffect(() => {
    if (isReturning && confirmation.isActive) goOn({ replace: true });
  }, [isReturning, confirmation.isActive, goOn]);

  const mode: 'confirming' | 'already-active' | 'paying' = isReturning
    ? 'confirming'
    : access === 'active' || checkout.alreadyActive
      ? 'already-active'
      : 'paying';

  return {
    mode,
    checkout: {
      clientSecret: checkout.clientSecret,
      isPending: checkout.isPending,
      isError: checkout.isError,
      retry: checkout.retry,
      price:
        checkout.amountInCents === null ? null : formatCurrency(checkout.amountInCents / 100, 0, 2),
    },
    confirmation,
    back: () => back('self-fund'),
    leaveUnconfirmed: () => (afterPurchase === 'your-plan' ? goTo('your-plan') : finish('options')),
    continueOn: () => goOn(),
  };
}
